import React, { useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { getCityNameKey } from '../../city/constants';
import { BASE_CITY_COORDINATES } from '../constants';

/** 도시 좌표로 현재 줌 레벨을 유지하며 부드럽게 이동 */
function CityPanHandler({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    map.panTo([target.lat, target.lng], { animate: true, duration: 0.8 });
  }, [map, target]);
  return null;
}

const myLocationIcon = L.divIcon({
  className: '',
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  html: `
    <style>
      @keyframes basePulse {
        0%   { transform: scale(1);   opacity: 0.6; }
        70%  { transform: scale(2.4); opacity: 0; }
        100% { transform: scale(2.4); opacity: 0; }
      }
    </style>
    <div style="position:relative; width:36px; height:36px; display:flex; align-items:center; justify-content:center;">
      <div style="
        position:absolute;
        width:20px; height:20px; border-radius:50%;
        background:rgba(59,130,246,0.35);
        animation: basePulse 2s ease-out infinite;
      "></div>
      <div style="
        position:relative;
        width:14px; height:14px; border-radius:50%;
        background:#3B82F6;
        border:2.5px solid #fff;
        box-shadow:0 2px 6px rgba(0,0,0,0.3);
        z-index:1;
      "></div>
    </div>
  `,
});

function createBaseCityIcon(isMyBase, isFull) {
  let bg, border;
  if (isMyBase)    { bg = '#059669'; border = '#D1FAE5'; }
  else if (isFull) { bg = '#9CA3AF'; border = '#F3F4F6'; }
  else             { bg = '#6C5CE7'; border = '#EDE9FE'; }

  return L.divIcon({
    className: '',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    html: `
      <div style="
        width: 28px; height: 28px; border-radius: 50%;
        background: ${bg};
        border: 3px solid ${border};
        box-shadow: 0 2px 8px rgba(0,0,0,0.28);
        display: flex; align-items: center; justify-content: center;
      ">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="#fff" stroke-width="2.5" stroke-linejoin="round"/>
          <polyline points="9,22 9,12 15,12 15,22" stroke="#fff" stroke-width="2.5" stroke-linejoin="round"/>
        </svg>
      </div>
    `,
  });
}

/**
 * BASE 탭 전용 Leaflet 세계 지도
 * - 줌/드래그/모든 인터랙션 비활성화 (정적 지도)
 * - 도시 핀 탭 시 해당 도시로 flyTo 이동
 * - OpenStreetMap 타일 사용 (무료, 글로벌)
 */
export default function BaseLeafletMap({ baseList = [], onCitySelect, panToCityId, myPos, height = 220 }) {
  const { t } = useTranslation();
  const [panTarget, setPanTarget] = React.useState(null);

  const baseMap = React.useMemo(() => {
    const m = {};
    baseList.forEach((item) => { if (item.cityId) m[item.cityId] = item; });
    return m;
  }, [baseList]);

  // 피드 카드 클릭 시 외부에서 cityId가 넘어오면 지도 이동
  useEffect(() => {
    if (!panToCityId) return;
    const coord = BASE_CITY_COORDINATES[panToCityId];
    if (coord) setPanTarget({ ...coord, _ts: Date.now() });
  }, [panToCityId]);

  const handlePinClick = useCallback((cityId) => {
    const coord = BASE_CITY_COORDINATES[cityId];
    if (coord) setPanTarget({ ...coord, _ts: Date.now() });
    if (onCitySelect) onCitySelect(cityId);
  }, [onCitySelect]);

  return (
    <div style={{ width: '100%', height, position: 'relative' }}>
      <MapContainer
        center={[20, 15]}
        zoom={2}
        style={{ width: '100%', height: '100%' }}
        // 인터랙션 전면 비활성화
        dragging={false}
        zoomControl={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        touchZoom={false}
        boxZoom={false}
        keyboard={false}
        attributionControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* 도시 핀 탭 시 해당 좌표로 이동 */}
        <CityPanHandler target={panTarget} />

        {/* 내 위치 마커 */}
        {myPos && (
          <Marker
            position={[myPos.lat, myPos.lng]}
            icon={myLocationIcon}
            interactive={false}
          />
        )}

        {/* BASE 도시 핀 */}
        {Object.entries(BASE_CITY_COORDINATES).map(([cityId, coord]) => {
          const item = baseMap[cityId];
          const isMyBase = item?.isMyBase === true;
          const isFull = item?.isFull === true;
          const cityName = t(getCityNameKey(cityId));
          const occupied = item?.occupiedCount ?? 0;
          const total = item?.slotsPerCity ?? 50;

          return (
            <Marker
              key={cityId}
              position={[coord.lat, coord.lng]}
              icon={createBaseCityIcon(isMyBase, isFull)}
              eventHandlers={{ click: () => handlePinClick(cityId) }}
            >
            </Marker>
          );
        })}
      </MapContainer>

    </div>
  );
}

